/**
 * The persistent PLATFORM TOP BAR: the app-level chrome shown above every
 * authenticated /app screen (the dashboard and every newsletter workspace). It is
 * the top of a three-level navigation hierarchy that the founder asked us to make
 * visually distinct:
 *   1. this top bar        - logo home, the newsletter switcher, the account menu
 *   2. the workspace nav   - Studio | Audience | Basics (inside a newsletter)
 *   3. the studio toolbar  - the editor's own Write | Wire modes
 *
 * The bar carries the newsletter NAME (via the switcher) so the per-screen headers
 * below it no longer need to repeat it. It fetches the owner's newsletter list once
 * (GET /me) to populate the switcher; a slow or failed load never crashes the bar,
 * it just falls back to the plain newsletter name. Both menus are real menus:
 * button + aria-expanded, Escape-dismissible, and closed on an outside click.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import type { Newsletter } from '@nuff/types';

import { getMe, type ApiDeps } from './api';
import { useAuth } from './auth/context';

/**
 * A dismissable-menu hook shared by the switcher and the account menu: tracks the
 * open flag, closes on Escape or an outside click, and hands back the root ref the
 * menu wraps so the outside-click test knows what "inside" means.
 */
function useDismissableMenu(): {
  readonly open: boolean;
  readonly setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  readonly root: React.RefObject<HTMLDivElement | null>;
} {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent): void => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return { open, setOpen, root };
}

/**
 * The NEWSLETTER SWITCHER: a menu button showing the current newsletter's name, with
 * a dropdown to jump between the owner's other newsletters plus an "All newsletters"
 * item back to the dashboard. Rendered only inside a newsletter (the bar shows a
 * plain "Your newsletters" label on the dashboard, where there is nothing to switch).
 */
function NewsletterSwitcher({
  activeId,
  activeName,
  newsletters,
}: {
  readonly activeId: string;
  readonly activeName: string;
  readonly newsletters: readonly Newsletter[];
}): React.JSX.Element {
  const { open, setOpen, root } = useDismissableMenu();
  const others = newsletters.filter((n) => n.newsletterId !== activeId);
  return (
    <div className="topbar__menu topbar__switcher" ref={root}>
      <button
        type="button"
        className="topbar__menu-btn topbar__switcher-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Current newsletter: ${activeName}. Switch newsletter`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="topbar__switcher-name">{activeName}</span>
        <span className="topbar__caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <ul className="topbar__menu-list" role="menu">
          {others.length > 0 && (
            <li className="topbar__menu-group" role="presentation">
              <span className="topbar__menu-label">Switch to</span>
            </li>
          )}
          {others.map((n) => (
            <li key={n.newsletterId} role="none">
              <Link
                role="menuitem"
                className="topbar__menu-item"
                to={`/app/newsletters/${n.newsletterId}`}
                onClick={() => setOpen(false)}
              >
                {n.name}
              </Link>
            </li>
          ))}
          <li role="none">
            <Link
              role="menuitem"
              className="topbar__menu-item topbar__menu-item--all"
              to="/app"
              onClick={() => setOpen(false)}
            >
              All newsletters
            </Link>
          </li>
        </ul>
      )}
    </div>
  );
}

/**
 * The ACCOUNT menu: the signed-in owner's email as a menu button, with a Sign out
 * action that clears the session and returns to sign-in (the same pattern the
 * dashboard used before the bar existed).
 */
function AccountMenu({
  email,
  onSignOut,
}: {
  readonly email: string;
  readonly onSignOut: () => void;
}): React.JSX.Element {
  const { open, setOpen, root } = useDismissableMenu();
  return (
    <div className="topbar__menu topbar__account" ref={root}>
      <button
        type="button"
        className="topbar__menu-btn topbar__account-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${email.length > 0 ? email : 'signed in'}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="topbar__account-email">{email.length > 0 ? email : 'Account'}</span>
        <span className="topbar__caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <ul className="topbar__menu-list topbar__menu-list--right" role="menu">
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className="topbar__menu-item"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
            >
              Sign out
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

export function TopBar(): React.JSX.Element {
  const { user, getIdToken, signOut } = useAuth();
  const navigate = useNavigate();

  // Keep the auth + navigation handles in refs so `deps` stays referentially STABLE.
  // react-router recreates `navigate` on every navigation, so a `deps` memoised on it
  // would change on each nav and re-run the GET /me effect below - refetching the
  // switcher list on every screen change. Ref-stabilising (like the workspace does)
  // means /me is fetched exactly once.
  const getIdTokenRef = useRef(getIdToken);
  getIdTokenRef.current = getIdToken;
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const deps = useMemo<ApiDeps>(
    () => ({
      getIdToken: () => getIdTokenRef.current(),
      onUnauthorized: () => {
        signOutRef.current();
        void navigateRef.current('/signin');
      },
    }),
    [],
  );

  // The newsletter list backing the switcher, fetched once. A failed/slow load leaves
  // it empty, so the switcher still shows the active name (from the URL match) without
  // ever crashing the bar.
  const [newsletters, setNewsletters] = useState<readonly Newsletter[]>([]);
  useEffect(() => {
    let active = true;
    getMe(deps)
      .then((me) => {
        if (active) setNewsletters(me.newsletters);
      })
      .catch(() => {
        /* The switcher degrades gracefully to the active name; never crash the bar. */
      });
    return () => {
      active = false;
    };
  }, [deps]);

  // The active newsletter id, read from the URL via a match so the bar works from any
  // nested workspace route (studio/audience/basics), not just a direct param render.
  const match = useMatch('/app/newsletters/:newsletterId/*');
  const activeId = match?.params.newsletterId ?? null;
  const active =
    activeId !== null ? (newsletters.find((n) => n.newsletterId === activeId) ?? null) : null;

  const handleSignOut = useCallback((): void => {
    signOut();
    void navigate('/signin');
  }, [signOut, navigate]);

  return (
    <header className="topbar">
      <Link className="topbar__brand" to="/app" aria-label="nuffsaid home">
        nuffsaid<span className="topbar__brand-dot">.cloud</span>
      </Link>

      {activeId !== null ? (
        <NewsletterSwitcher
          activeId={activeId}
          activeName={active?.name ?? 'Newsletter'}
          newsletters={newsletters}
        />
      ) : (
        <span className="topbar__here">Your newsletters</span>
      )}

      <div className="topbar__spacer" />

      <AccountMenu email={user?.email ?? ''} onSignOut={handleSignOut} />
    </header>
  );
}
